import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Mail, CheckCircle } from 'lucide-react';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || '';
  
  const [email, setEmail] = useState(emailFromState);
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, otp })
      });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      
      toast.success('Email verified successfully!');
      navigate('/portal/dashboard');
    } catch (error) {
      toast.error(error.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: 'resend-trigger' })
      });
      toast.info('If your account exists, a new code has been sent.');
    } catch (error) {
      toast.info('If your account exists, a new code has been sent.');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-100 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-navy font-serif mb-2">Verify Your Email</h2>
          <p className="text-slate-600 mb-6">
            We've sent a 6-digit code to <strong>{email || 'your email'}</strong>
          </p>
          
          <form onSubmit={handleVerify} className="space-y-4">
            {!emailFromState && (
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="h-12"
                required
                data-testid="verify-email-input"
              />
            )}
            <Input
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter 6-digit code"
              className="text-center text-2xl tracking-widest h-14"
              maxLength={6}
              required
              data-testid="verify-otp-input"
            />
            <Button
              type="submit"
              disabled={loading || otp.length !== 6}
              className="w-full h-12 bg-navy hover:bg-slate-800 text-white rounded-full"
              data-testid="verify-submit-btn"
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </Button>
          </form>

          <p className="mt-4 text-sm text-slate-500">
            Didn't receive the code?{' '}
            <button onClick={handleResend} className="text-amber-600 hover:text-amber-700 font-medium">
              Resend
            </button>
          </p>

          <p className="mt-6">
            <Link to="/login" className="text-slate-500 hover:text-slate-700 text-sm">
              ← Back to Login
            </Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
